import axios from 'axios';

// Use environment variable provided by webpack EnvironmentPlugin
// Prefer API_URL which points to the auth API service in .env
const { API_URL = '' } = process.env

// Create a new axios instance with the env base URL
const authRequest = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  },
  withCredentials: false // Keep this false to avoid CORS preflight issues
});

// Add request interceptor to include auth token
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Function to validate token
const isTokenValid = (token) => {
  if (!token) return false;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 > Date.now();
  } catch (e) {
    return false;
  }
};

authRequest.interceptors.request.use(
  async (config) => {
    // Get token from localStorage or sessionStorage
    let token = localStorage.getItem('token') || sessionStorage.getItem('token');
    
    // If token exists, add it to the headers
    if (token) {
      // Only validate token if it exists, but don't block requests if invalid
      if (!isTokenValid(token)) {
        console.log('Token appears expired, but proceeding with request. Server will handle auth.');
      }
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.log('No token found, proceeding without authentication header');
    }
    
    // Log the request URL for debugging (defensive join)
    try {
      const base = (config.baseURL || '').replace(/\/$/, '')
      const url = (config.url || '').startsWith('/') ? (config.url || '') : `/${config.url || ''}`
      console.log(`Making request to: ${base}${url}`)
    } catch (e) {}
    
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor to handle errors
authRequest.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If the error is 401 and we haven't tried to refresh the token yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      console.log('Received 401 error, attempting token refresh...');
      
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return authRequest(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Try to refresh the token
        const refreshToken = localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');
        if (refreshToken) {
          console.log('Attempting to refresh token...');
          // Use the same axios instance to respect baseURL from env
          const response = await authRequest.post('/auth/refresh-token', { refreshToken });
          const { token } = response.data;
          
          // Store the new token
          localStorage.setItem('token', token);
          
          // Update the Authorization header
          authRequest.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          originalRequest.headers.Authorization = `Bearer ${token}`;
          
          // Process any queued requests
          processQueue(null, token);
          
          console.log('Token refreshed successfully, retrying original request');
          return authRequest(originalRequest);
        } else {
          // No refresh token available, redirect to login
          console.warn('No refresh token available, redirecting to login');
          localStorage.removeItem('token');
          sessionStorage.removeItem('token');
          window.location.href = '/login';
          return Promise.reject(error);
        }
      } catch (refreshError) {
        // If refresh fails, clear tokens and redirect to login
        console.error('Token refresh failed, redirecting to login:', refreshError);
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('refreshToken');
        window.location.href = '/login';
        processQueue(refreshError, null);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // For other errors, just pass them through
    return Promise.reject(error);
  }
);

export default authRequest
